/**
 * HUD — DOM overlay. Faction palette comes from data (factions.json),
 * labels from localization_en.json via db.t(). Kept in DOM (not WebGL) for
 * fast iteration; the visual spec transfers from the main repo's HUD docs.
 */
import type { GameDB } from "../data/gamedb";
import type { Economy } from "../sim/economy";
import type { FactionDef } from "../data/types";
import type { CommandStatus } from "../sim/command";
import { iconImg, resourceIcon } from "./icons";

export class Hud {
  private top = document.getElementById("hud-top")!;
  private bottom = document.getElementById("hud-bottom")!;
  private clock = document.createElement("div");
  /** Medallion slot (unit portrait) at the left of the selection bar. */
  private medallionSlot = document.createElement("span");
  /** Selection label text, kept separate so medallions survive refreshes. */
  private selectionLabel = document.createElement("span");
  /** Military command pool (set by the coordinator each HUD tick). */
  private command: CommandStatus | null = null;

  constructor(
    private readonly db: GameDB,
    private readonly economy: Economy,
    faction: FactionDef,
  ) {
    this.top.style.borderBottomColor = faction.palette.primary;
    this.bottom.style.borderTopColor = faction.palette.primary;
    this.clock.id = "hud-clock";
    this.clock.style.cssText =
      "position:fixed;top:54px;left:50%;transform:translateX(-50%);z-index:30;" +
      "background:rgba(10,10,12,0.82);border:1px solid #b98b3a;border-radius:6px;" +
      "padding:4px 12px;font-size:13px;color:#e8e2d4;pointer-events:none;";
    document.body.appendChild(this.clock);
    document.title = `${db.t("game_title", "Legionnaire")} — ${faction.name_en} (web prototype)`;
    // Selection bar: medallion portrait + label as separate spans so the
    // label refreshes never wipe the portrait canvas.
    this.bottom.innerHTML = "";
    this.bottom.style.display = "flex";
    this.bottom.style.alignItems = "center";
    this.medallionSlot.id = "hud-medallion";
    this.medallionSlot.style.cssText =
      "display:inline-block;vertical-align:middle;margin-right:10px;flex:0 0 auto;";
    this.selectionLabel.id = "hud-selection";
    this.selectionLabel.innerHTML =
      "Click a formation to select it. Right-click to order it to move.";
    this.bottom.appendChild(this.medallionSlot);
    this.bottom.appendChild(this.selectionLabel);
  }

  /** Day/night clock (top-center). Called every frame by the coordinator. */
  setClock(label: string, night: boolean): void {
    this.clock.textContent = label;
    this.clock.style.borderColor = night ? "#4a5a8a" : "#b98b3a";
  }

  /** Military command pool for the top bar (1 formation = 1 command). */
  setCommand(status: CommandStatus): void {
    this.command = status;
  }

  /** Empire military upkeep for the top bar (food/min on paper strength). */
  setUpkeep(foodPerMin: number): void {
    this.upkeepFoodPerMin = foodPerMin;
  }
  private upkeepFoodPerMin = 0;

  /**
   * Transient notices (training confirmations, cap warnings, AI alerts)
   * persist for a few seconds: the coordinator's selection-label refresh
   * must not wipe them the next tick.
   */
  private noticeUntil = 0;
  noticeActive(): boolean {
    return performance.now() < this.noticeUntil;
  }

  refresh(): void {
    const parts: string[] = [];
    for (const def of this.economy.resourceDefs()) {
      const amount = Math.floor(this.economy.stocks.get(def.id) ?? 0);
      const rate = this.economy.rates.get(def.id) ?? 0;
      const rateStr = Math.abs(rate) > 0.005 ? ` (${rate >= 0 ? "+" : ""}${rate.toFixed(1)}/s)` : "";
      parts.push(
        `<span class="res">${iconImg(resourceIcon(def.id), def.name_en, 16)}<span class="dot" style="background:${def.colour}"></span>` +
          `${this.db.t(`res_${def.id}`, def.name_en)}: <b>${amount}</b>${rateStr}</span>`,
      );
    }
    if (this.command) {
      const c = this.command;
      const warn = c.used >= c.total ? "color:#c96a5a;" : "";
      parts.push(
        `<span class="res" style="${warn}" title="Military command: 1 formation = 1 command. Build military buildings or research Dilectus to raise the cap.">` +
          `⌘ Command: <b>${c.used}/${c.total}</b></span>`,
      );
    }
    if (this.upkeepFoodPerMin > 0.005) {
      parts.push(
        `<span class="res" title="Military upkeep: food/min across all formations, charged on paper strength (0.2 × n^0.85 per formation).">` +
          `🍞 Upkeep: <b>${this.upkeepFoodPerMin.toFixed(1)}</b>/min</span>`,
      );
    }
    this.top.innerHTML = parts.join("");
  }

  showSelection(html: string): void {
    this.selectionLabel.innerHTML = html;
    this.noticeUntil = performance.now() + 3000;
  }

  /** Unit medallion portrait for the selection bar (null clears it). */
  setMedallion(canvas: HTMLCanvasElement | null): void {
    this.medallionSlot.innerHTML = "";
    if (canvas) this.medallionSlot.appendChild(canvas);
  }

  /**
   * Recurring status line (e.g. the selection label). Does not arm the
   * notice guard — the coordinator calls this every HUD tick.
   */
  showStatus(html: string): void {
    this.selectionLabel.innerHTML = html;
  }
}
